import { AnimatePresence } from 'framer-motion';
import { ArrowRight, CloudSun } from 'lucide-react';
import type { ForecastDaySummary } from './WeatherDashboard';
import type { ForecastSummaryBullets } from '../../lib/weatherForecastSummary';
import { TourStyleCard } from '../ui/TourStyleCard';
import { WeatherConditionsWarning } from './WeatherConditionsWarning';
import { WeatherCodeIcon } from './WeatherCodeIcon';
import { WeatherSubscribeFooter } from './WeatherSubscribeFooter';

interface WeatherDailyBriefingProps {
  open: boolean;
  loading?: boolean;
  summary: ForecastSummaryBullets | null;
  today: ForecastDaySummary | null;
  districtName?: string | null;
  postcode?: string | null;
  isSubscribed?: boolean;
  onDismiss: () => void;
  onSubscribe: () => void;
  onOpenFullForecast: () => void;
}

/** Once-per-day today outlook on the main map — product-tour card styling. */
export function WeatherDailyBriefing({
  open,
  loading = false,
  summary,
  today,
  districtName,
  postcode,
  isSubscribed = false,
  onDismiss,
  onSubscribe,
  onOpenFullForecast,
}: WeatherDailyBriefingProps) {
  const locationLine = [districtName, postcode].filter(Boolean).join(' · ');
  const title = districtName ? `Today in ${districtName}` : 'Today\'s outlook';

  return (
    <AnimatePresence>
      {open && (
        <TourStyleCard
          headerIcon={<CloudSun size={16} className="shrink-0 text-white" />}
          headerLabel="Weather briefing"
          title={title}
          onDismiss={onDismiss}
          dismissLabel="Dismiss weather briefing"
          footer={
            <WeatherSubscribeFooter
              isSubscribed={isSubscribed}
              onSubscribe={onSubscribe}
              dismissLabel="Not now"
              onDismiss={onDismiss}
              extra={
                <button
                  type="button"
                  onClick={onOpenFullForecast}
                  className="w-full py-2 text-xs font-medium text-emerald-600 transition-colors hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 flex items-center justify-center gap-1 touch-manipulation"
                >
                  See full forecast
                  <ArrowRight size={12} aria-hidden />
                </button>
              }
            />
          }
        >
          {locationLine && (
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{locationLine}</p>
          )}

          {loading ? (
            <div className="space-y-2.5 animate-pulse" aria-hidden>
              <span className="block h-10 w-full rounded-xl bg-gray-100 dark:bg-gray-800" />
              <span className="block h-3 w-full rounded bg-gray-200 dark:bg-gray-700" />
              <span className="block h-3 w-[85%] rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          ) : (
            <>
              {today && (
                <div className="mb-3 flex items-center gap-3 rounded-xl border border-gray-200/80 bg-gray-50 px-3 py-2.5 dark:border-gray-700/60 dark:bg-gray-800/60">
                  <WeatherCodeIcon code={today.weather_code} size={32} />
                  <div className="min-w-0">
                    {today.temperature_c != null && (
                      <p className="text-lg font-semibold tabular-nums leading-none text-gray-900 dark:text-white">
                        {Math.round(today.temperature_c)}°C
                      </p>
                    )}
                    {today.precipitation_mm != null && today.precipitation_mm > 0 && (
                      <p className="mt-1 text-xs text-sky-600 dark:text-sky-400">
                        {today.precipitation_mm.toFixed(0)} mm rain expected
                      </p>
                    )}
                  </div>
                </div>
              )}

              {summary ? (
                <ul className="space-y-2.5 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                  <li>{summary.today}</li>
                  {summary.conditions && (
                    <li>
                      <WeatherConditionsWarning message={summary.conditions} />
                    </li>
                  )}
                </ul>
              ) : (
                <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  Fetching today&apos;s forecast for your area. This usually takes a moment.
                </p>
              )}
            </>
          )}
        </TourStyleCard>
      )}
    </AnimatePresence>
  );
}
